<script setup>
import { formatDate, addThousandsSeparator, getPriceChangeClass } from '~/utils/utils.js';
import Card from '~/components/Card.vue';
import InfoTooltip from '../InfoTooltip.vue';
import LoadingSkeleton from '~/components/LoadingSkeleton.vue';
import CardLabel from '~/components/CardLabel.vue';

// Props remain the same
const props = defineProps({
    priceChange24h: { type: Number, default: 0 },
    priceChange7d: { type: Number, default: 0 },
    priceChange30d: { type: Number, default: 0 },
    allTimeHigh: { type: Number, default: 0 },
    allTimeHighDate: { type: String, default: '' },
    allTimeHighChangePercentage: { type: Number, default: 0 },
    allTimeHighChangeValue: { type: Number, default: 0 },
    allTimeLow: { type: Number, default: 0 },
    allTimeLowDate: { type: String, default: '' },
    allTimeLowChangePercentage: { type: Number, default: 0 },
    allTimeLowChangeValue: { type: Number, default: 0 },
    loading: { type: Boolean, default: false }
});
</script>

<template>
    <div class="grid grid-cols-1 xl:grid-cols-3 cards-gap">
        <!-- Price Changes Card -->
        <Card>
            <div class="flex items-center mb-3">
                <CardLabel text="Price Changes" />
                <InfoTooltip text="The percentage change in price over the last 24 hours, 7 days, and 30 days" />
            </div>
            <div class="grid grid-cols-3 gap-4">
                <!-- 24h Change -->
                <div class="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-3">
                    <span class="text-gray-500 dark:text-gray-400 text-sm mb-1 block">24h</span>
                    <LoadingSkeleton v-if="props.loading" height="28px" width="80px" />
                    <div v-else class="slide-in-up">
                        <span class="text-lg font-bold" :class="getPriceChangeClass(props.priceChange24h)">
                            {{ addThousandsSeparator(props.priceChange24h, 2) }}%
                        </span>
                    </div>
                </div>

                <!-- 7d Change -->
                <div class="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-3">
                    <span class="text-gray-500 dark:text-gray-400 text-sm mb-1 block">7d</span>
                    <LoadingSkeleton v-if="props.loading" height="28px" width="80px" />
                    <div v-else class="slide-in-up">
                        <span class="text-lg font-bold" :class="getPriceChangeClass(props.priceChange7d)">
                            {{ addThousandsSeparator(props.priceChange7d, 2) }}%
                        </span>
                    </div>
                </div>

                <!-- 30d Change -->
                <div class="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-3">
                    <span class="text-gray-500 dark:text-gray-400 text-sm mb-1 block">30d</span>
                    <LoadingSkeleton v-if="props.loading" height="28px" width="80px" />
                    <div v-else class="slide-in-up">
                        <span class="text-lg font-bold" :class="getPriceChangeClass(props.priceChange30d)">
                            {{ addThousandsSeparator(props.priceChange30d, 2) }}%
                        </span>
                    </div>
                </div>
            </div>
        </Card>

        <!-- All Time High -->
        <Card class="flex items-center">
            <div class="flex justify-between w-full">
                <div class="flex-1">
                    <div class="flex items-center sm:space-x-2 mb-3">
                        <div class="p-2 bg-green-50 dark:bg-green-900/20 rounded-lg hidden sm:block">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-green-600 dark:text-green-400" viewBox="0 0 20 20" fill="currentColor">
                                <path d="M5 10l5-5 5 5H5z" />
                                <path d="M5 15h10v-2H5v2z" />
                            </svg>
                        </div>
                        <CardLabel text="All Time High" />
                        <InfoTooltip text="The highest price of KAS in history" />
                    </div>
                    <div class="mb-0 sm:mb-1.5">
                        <LoadingSkeleton v-if="props.loading" height="28px" width="100px" />
                        <div v-else class="slide-in-up">
                            <span class="text-lg sm:text-xl font-bold" :class="getPriceChangeClass(1)">
                                ${{ addThousandsSeparator(props.allTimeHigh, 4) }}
                            </span>
                        </div>
                    </div>
                    <div>
                        <LoadingSkeleton v-if="props.loading" height="24px" width="140px" />
                        <div v-else class="slide-in-up">
                            <span class="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                                {{ formatDate(props.allTimeHighDate) }}
                            </span>
                        </div>
                    </div>
                </div>
                <div class="pl-6 ml-6 border-l border-gray-100 dark:border-gray-700">
                    <div class="flex items-center space-x-2 sm:h-[32px] mb-3">
                        <CardLabel text="From ATH" />
                        <InfoTooltip text="The percentage and value change from the all-time high" />
                    </div>
                    <div class="mb-0 sm:mb-1.5">
                        <LoadingSkeleton v-if="props.loading" height="28px" width="100px" />
                        <div v-else class="slide-in-up">
                            <span class="text-lg sm:text-xl font-bold" :class="getPriceChangeClass(-1)">
                                {{ addThousandsSeparator(props.allTimeHighChangePercentage, 2) }}%
                            </span>
                        </div>
                    </div>
                    <div>
                        <LoadingSkeleton v-if="props.loading" height="24px" width="88px" />
                        <div v-else class="slide-in-up">
                            <span class="text-xs sm:text-sm" :class="getPriceChangeClass(-1)">
                                -${{ addThousandsSeparator(props.allTimeHighChangeValue, 4) }}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </Card>

        <!-- All Time Low -->
        <Card class="flex items-center">
            <div class="flex justify-between w-full">
                <div class="flex-1">
                    <div class="flex items-center sm:space-x-2 mb-3">
                        <div class="p-2 bg-red-50 dark:bg-red-900/20 rounded-lg hidden sm:block">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-red-600 dark:text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                <path d="M5 10l5 5 5-5H5z" />
                                <path d="M5 5h10v2H5V5z" />
                            </svg>
                        </div>
                        <CardLabel text="All Time Low" />
                        <InfoTooltip text="The lowest price of KAS in history" />
                    </div>
                    <div class="mb-0 sm:mb-1.5">
                        <LoadingSkeleton v-if="props.loading" height="28px" width="100px" />
                        <div v-else class="slide-in-up">
                            <span class="text-lg sm:text-xl font-bold" :class="getPriceChangeClass(-1)">
                                ${{ addThousandsSeparator(props.allTimeLow, 4) }}
                            </span>
                        </div>
                    </div>
                    <div>
                        <LoadingSkeleton v-if="props.loading" height="24px" width="140px" />
                        <div v-else class="slide-in-up">
                            <span class="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                                {{ formatDate(props.allTimeLowDate) }}
                            </span>
                        </div>
                    </div>
                </div>
                <div class="pl-6 ml-6 border-l border-gray-100 dark:border-gray-700">
                    <div class="flex items-center space-x-2 sm:h-[32px] mb-3">
                        <CardLabel text="From ATL" />
                        <InfoTooltip text="The percentage and value change from the all-time low" />
                    </div>
                    <div class="mb-0 sm:mb-1.5">
                        <LoadingSkeleton v-if="props.loading" height="28px" width="100px" />
                        <div v-else class="slide-in-up">
                            <span class="text-lg sm:text-xl font-bold" :class="getPriceChangeClass(1)">
                                +{{ addThousandsSeparator(props.allTimeLowChangePercentage, 2) }}%
                            </span>
                        </div>
                    </div>
                    <div>
                        <LoadingSkeleton v-if="props.loading" height="24px" width="88px" />
                        <div v-else class="slide-in-up">
                            <span class="text-xs sm:text-sm" :class="getPriceChangeClass(1)">
                                +${{ addThousandsSeparator(props.allTimeLowChangeValue, 4) }}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </Card>
    </div>
</template>